import { type ReactNode, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import clsx from 'clsx'

interface Props {
  open: boolean
  onClose?: () => void
  title?: string
  children: ReactNode
  /** When true, clicking backdrop does NOT close. */
  persistent?: boolean
  className?: string
}

export function Modal({ open, onClose, title, children, persistent, className }: Props) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !persistent) onClose?.()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, persistent, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-sage-900/40 px-3 pb-3 sm:items-center sm:pb-0"
          // Leave room for the TG client header (account avatar / close
          // button row) on top — without it tall modals slide under the
          // header and the top section gets clipped. CSS env() reads the
          // real device safe-area when available, max() guards short
          // viewports where TG doesn't report a safe-area inset.
          style={{
            paddingTop:
              'max(env(safe-area-inset-top, 0px), var(--tg-viewport-stable-height-offset, 56px))',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => { if (!persistent) onClose?.() }}
        >
          <motion.div
            // max-h + overflow lets tall modals scroll inside instead of
            // pushing the top out of the visible area.
            className={clsx(
              'w-full max-w-md max-h-[88vh] overflow-y-auto rounded-3xl bg-cream p-4 shadow-xl ring-1 ring-sage-200/60 sm:p-5',
              className,
            )}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <header className="mb-3">
                <h2 className="font-serif text-2xl text-sage-800">{title}</h2>
              </header>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
